import React from 'react'
import { useParams } from 'react-router-dom'
import { Box, Card, Grid, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'

import {
  PhishingParticipantsTable,
  PhishingResultsChart,
  PhishingStatsCards,
  TemplateStatsCard,
} from 'views/Phishing/components'
import TemplateCard from '../TemplateStep/components/TemplateCard'

import useFetch from 'hooks/useFetch'
import usePagination from 'hooks/usePagination'
import usePhishing from 'hooks/usePhishing'
import * as service from 'service'
import constants from 'constants/index'

import useStyles from './styles'

const DetailsStep = () => {
  const classes = useStyles()
  const { phishingId } = useParams()
  const { phishing } = usePhishing()

  const isSending = phishing?.status === constants.phishing.SENDING_STATUS_ID
  const daysUntilExpiration = phishing?.daysUntilExpiration

  const expirationLabel =
    daysUntilExpiration == null
      ? null
      : daysUntilExpiration > 1
      ? `Expira em ${daysUntilExpiration} dias`
      : daysUntilExpiration === 1
      ? 'Expira em 1 dia'
      : daysUntilExpiration === 0
      ? 'Expira hoje'
      : 'Prazo expirado'

  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(10)

  const summary = phishing?.phishingCampaignSummary

  const stats = summary
    ? {
        delivered: summary.delivered ?? 0,
        viewed: summary.opened ?? 0,
        clicks: summary.clicked ?? 0,
        compromised: summary.compromised ?? 0,
        completed: summary.courseCompleted ?? 0,
      }
    : null

  const { response: dailyStatsResponse } = useFetch(
    service.dponet.phishingCampaigns.dailyStats,
    phishingId,
  )

  const dailyStats = dailyStatsResponse?.data?.phishingCampaignDailyStats ?? []

  const needsTemplateFetch =
    !phishing?.phishingTemplate && !!phishing?.phishingTemplateId
  const { response: templateResponse } = useFetch(
    service.dponet.phishingTemplates.show,
    phishing?.phishingTemplateId,
    [],
    needsTemplateFetch,
  )

  const phishingTemplate =
    phishing?.phishingTemplate || templateResponse?.data?.phishingTemplate

  const { response: courseResponse } = useFetch(
    service.dponet.trainingCourse.show,
    { courseId: phishing?.trainingId },
    [phishing?.trainingId],
    !!phishing?.trainingId,
  )

  const trainingCourse = courseResponse?.data?.course

  const {
    response: participantStatesResponse,
    isLoading: isLoadingParticipants,
  } = useFetch(
    () =>
      service.dponet.phishingCampaigns.participantStates(phishingId, {
        page,
        perPage,
      }),
    null,
    [page, perPage],
    !!phishingId,
  )

  const participantStatesData =
    participantStatesResponse?.data?.phishingCampaignParticipants ?? []
  const participantStatesCount =
    participantStatesResponse?.data?.meta?.totalCount ?? 0

  const participants = participantStatesData.map((participant) => ({
    id: participant.id,
    name: participant.name,
    email: participant.email,
    department: participant.departmentName,
    viewed: !!participant.openedAt,
    clicks: !!participant.clickedAt,
    compromised: !!participant.compromisedAt,
    courseCompleted: !!participant.courseCompleted,
    status: participant.failed ? 'failed' : participant.displayStatus,
    date: participant.lastEventAt || participant.sentAt,
  }))

  return (
    <Box>
      {isSending && expirationLabel && (
        <Box mb={3}>
          <Card>
            <Box
              p={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h5">Prazo de finalização</Typography>
              <Typography variant="body1" color="error">
                {expirationLabel}
              </Typography>
            </Box>
          </Card>
        </Box>
      )}
      {isSending && (
        <Box mb={3}>
          <Alert severity="info">
            Os indicadores são atualizados a cada 10 minutos.
          </Alert>
        </Box>
      )}
      <PhishingStatsCards stats={stats} />
      <Box mt={3}>
        <PhishingResultsChart
          stats={stats}
          dailyStats={dailyStats}
          hasCourse={!!phishing?.trainingId}
        />
      </Box>
      <Box mt={3}>
        <PhishingParticipantsTable
          participants={participants}
          isLoading={isLoadingParticipants}
          count={participantStatesCount}
          summary={summary}
          hasCourse={!!phishing?.trainingId}
          pagination={{
            perPage,
            page,
            handleChangePage,
            handleChangeRowsPerPage,
          }}
        />
      </Box>
      {(phishingTemplate || trainingCourse) && (
        <Box mt={3} display="flex" gridGap={24} flexWrap="wrap">
          {phishingTemplate && (
            <Box className={classes.splitBox}>
              <Card className={classes.fullHeightCard}>
                <Box p={3}>
                  <Typography variant="h3">Modelo utilizado</Typography>
                  <Box mt={2}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={5} md={5} lg={7}>
                        <TemplateCard
                          template={phishingTemplate}
                          isSelected
                          isReadOnly
                          hideActions
                          noShadow
                          fitContent
                          onSelect={() => {}}
                          onPreview={() => {}}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TemplateStatsCard
                          summary={summary}
                          template={phishingTemplate}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Card>
            </Box>
          )}
          {trainingCourse && (
            <Box className={classes.splitBox}>
              <Card className={classes.fullHeightCard}>
                <Box p={3}>
                  <Typography variant="h3">Curso vinculado</Typography>
                  <Box mt={2} className={classes.courseBox}>
                    {trainingCourse.thumbnailUrl && (
                      <Box
                        component="img"
                        className={classes.courseImage}
                        src={trainingCourse.thumbnailUrl}
                        alt={trainingCourse.title}
                        width="100%"
                      />
                    )}
                    <Box p={2}>
                      <Typography variant="h5">
                        {trainingCourse.title}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default DetailsStep
